import { Component } from '@theme/component';

const SWIPE_THRESHOLD = 40;
const EASE_IN = 'cubic-bezier(0.55, 0, 1, 0.45)';
const EASE_OUT = 'cubic-bezier(0.22, 1, 0.36, 1)';

/**
 * Carousel for the Liebesjuwelier announcement bar.
 *
 * Slides move vertically. The hairline at the bottom of the bar is a Web Animation whose end
 * advances the carousel, so every pause (hover, keyboard focus, hidden tab, theme editor)
 * freezes the timer exactly where it is and resumes from there. There are no arrow or pause
 * buttons; touch users can swipe between slides.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} viewport
 * @property {HTMLElement} progress
 * @property {HTMLElement[]} [slides]
 *
 * @extends {Component<Refs>}
 */
export class LjAnnouncementBar extends Component {
  requiredRefs = ['viewport', 'progress'];

  #index = 0;

  /** @type {Animation | undefined} */
  #timer;

  /** @type {Animation[]} */
  #transitions = [];

  /** @type {AbortController | undefined} */
  #abortController;

  /**
   * Reasons that currently hold the rotation: 'hover', 'focus', 'hidden', 'editor'.
   * @type {Set<string>}
   */
  #holds = new Set();

  /** @type {{ x: number, y: number } | undefined} */
  #pointerStart;

  /** Clicks before this timestamp belong to a swipe and must not open a slide link. */
  #suppressClickUntil = 0;

  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const { viewport } = this.refs;

    this.addEventListener('pointerenter', this.#handlePointerEnter, { signal });
    this.addEventListener('pointerleave', () => this.#release('hover'), { signal });
    this.addEventListener('focusin', this.#handleFocusIn, { signal });
    this.addEventListener('focusout', this.#handleFocusOut, { signal });
    this.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });
    this.addEventListener('shopify:block:deselect', () => this.#release('editor'), { signal });
    viewport.addEventListener('pointerdown', this.#handlePointerDown, { signal });
    viewport.addEventListener('pointerup', this.#handlePointerUp, { signal });
    viewport.addEventListener('pointercancel', () => (this.#pointerStart = undefined), { signal });
    viewport.addEventListener('click', this.#handleClick, { signal, capture: true });
    document.addEventListener('visibilitychange', this.#handleVisibilityChange, { signal });

    if (document.hidden) this.#holds.add('hidden');

    this.#syncSlides();
    this.setAttribute('ready', '');
    this.#startTimer();
  }

  updatedCallback() {
    super.updatedCallback();

    const count = this.#slides.length;
    this.#index = count ? Math.min(this.#index, count - 1) : 0;
    this.#syncSlides();
    this.#startTimer();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#timer?.cancel();
    this.#timer = undefined;
    for (const animation of this.#transitions) animation.cancel();
    this.#transitions = [];
  }

  /**
   * Shows the next slide.
   */
  next() {
    this.#goTo(this.#index + 1, 1);
  }

  /**
   * Shows the previous slide.
   */
  previous() {
    this.#goTo(this.#index - 1, -1);
  }

  /**
   * Autoplay interval in milliseconds, or 0 when autoplay is off.
   */
  get interval() {
    const seconds = Number.parseInt(this.getAttribute('autoplay') ?? '', 10);
    return Number.isNaN(seconds) ? 0 : seconds * 1000;
  }

  get #slides() {
    return this.refs.slides ?? [];
  }

  /**
   * @param {number} index - Target index, wraps around in both directions.
   * @param {1 | -1} direction - 1 moves the slides up (next), -1 moves them down (previous).
   */
  #goTo(index, direction) {
    const slides = this.#slides;
    const count = slides.length;
    if (count < 2) return;

    const target = ((index % count) + count) % count;
    if (target === this.#index) return;

    const outgoing = slides[this.#index];
    const incoming = slides[target];
    this.#index = target;

    if (outgoing && incoming) this.#animateSwap(outgoing, incoming, direction);
    this.#syncSlides();
    this.#startTimer();
  }

  /**
   * @param {HTMLElement} outgoing
   * @param {HTMLElement} incoming
   * @param {1 | -1} direction
   */
  #animateSwap(outgoing, incoming, direction) {
    for (const animation of this.#transitions) animation.cancel();

    const reduce = this.#reducedMotion.matches;
    const distance = reduce ? 0 : 100;

    outgoing.setAttribute('data-leaving', '');

    const leave = outgoing.animate(
      [
        { opacity: 1, transform: 'translateY(0)' },
        { opacity: 0, transform: `translateY(${-direction * distance}%)` },
      ],
      { duration: reduce ? 200 : 360, easing: EASE_IN }
    );

    const enter = incoming.animate(
      [
        { opacity: 0, transform: `translateY(${direction * distance}%)` },
        { opacity: 1, transform: 'translateY(0)' },
      ],
      { duration: reduce ? 200 : 640, delay: reduce ? 0 : 90, easing: EASE_OUT, fill: 'backwards' }
    );

    const cleanUp = () => outgoing.removeAttribute('data-leaving');
    leave.finished.then(cleanUp, cleanUp);

    this.#transitions = [leave, enter];
  }

  #syncSlides() {
    const slides = this.#slides;
    const count = slides.length;
    const label = this.dataset.slideLabel;

    slides.forEach((slide, index) => {
      const active = index === this.#index;

      slide.setAttribute('aria-hidden', `${!active}`);
      slide.inert = !active;

      if (count > 1) {
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', this.dataset.slideRole ?? 'slide');
        if (label) {
          slide.setAttribute('aria-label', label.replace('[index]', `${index + 1}`).replace('[count]', `${count}`));
        }
      }
    });
  }

  #startTimer() {
    this.#timer?.cancel();
    this.#timer = undefined;

    if (!this.interval || this.#slides.length < 2) {
      this.#syncState();
      return;
    }

    const timer = this.refs.progress.animate([{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }], {
      duration: this.interval,
      easing: 'linear',
    });

    timer.finished.then(
      () => {
        if (this.#timer === timer) this.next();
      },
      () => {}
    );

    this.#timer = timer;
    if (this.#holds.size) timer.pause();
    this.#syncState();
  }

  /**
   * @param {string} reason
   */
  #hold(reason) {
    this.#holds.add(reason);
    this.#timer?.pause();
    this.#syncState();
  }

  /**
   * @param {string} reason
   */
  #release(reason) {
    if (!this.#holds.delete(reason)) return;

    if (!this.#holds.size) {
      if (this.#timer) {
        this.#timer.play();
      } else {
        this.#startTimer();
      }
    }

    this.#syncState();
  }

  /**
   * Keeps the live region in line with the rotation state. Screen readers only hear slide
   * changes while keyboard focus holds the rotation, so autoplay never interrupts them.
   */
  #syncState() {
    const rotating = Boolean(this.interval) && !this.#holds.has('focus');
    this.refs.viewport.setAttribute('aria-live', rotating ? 'off' : 'polite');
  }

  /**
   * @param {PointerEvent} event
   */
  #handlePointerEnter = (event) => {
    if (event.pointerType === 'mouse') this.#hold('hover');
  };

  /**
   * Only keyboard focus stops the rotation; a mouse click on an arrow should not.
   * @param {FocusEvent} event
   */
  #handleFocusIn = (event) => {
    if (event.target instanceof Element && event.target.matches(':focus-visible')) this.#hold('focus');
  };

  /**
   * @param {FocusEvent} event
   */
  #handleFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.#release('focus');
  };

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.#hold('hidden');
    } else {
      this.#release('hidden');
    }
  };

  /**
   * Shows the selected slide while it is edited in the theme editor.
   * @param {Event} event
   */
  #handleBlockSelect = (event) => {
    if (!(event.target instanceof Element)) return;

    const slide = event.target.closest('[data-lj-slide]');
    const index = this.#slides.findIndex((item) => item === slide);

    this.#hold('editor');
    if (index >= 0) this.#goTo(index, index < this.#index ? -1 : 1);
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    if (event.pointerType === 'mouse') return;
    this.#pointerStart = { x: event.clientX, y: event.clientY };
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerUp = (event) => {
    const start = this.#pointerStart;
    this.#pointerStart = undefined;
    if (!start) return;

    const deltaX = event.clientX - start.x;
    const deltaY = event.clientY - start.y;
    if (Math.abs(deltaX) < SWIPE_THRESHOLD || Math.abs(deltaX) < Math.abs(deltaY)) return;

    this.#suppressClickUntil = event.timeStamp + 500;

    const isRtl = getComputedStyle(this).direction === 'rtl';
    const forward = isRtl ? deltaX > 0 : deltaX < 0;

    if (forward) {
      this.next();
    } else {
      this.previous();
    }
  };

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (event.timeStamp > this.#suppressClickUntil) return;

    event.preventDefault();
    event.stopPropagation();
  };
}

if (!customElements.get('lj-announcement-bar')) {
  customElements.define('lj-announcement-bar', LjAnnouncementBar);
}
